import React from 'react'
import { Form } from 'react-bootstrap'
import { ConfirmationModal } from '@components/common'
import Button from '@components/common/Button/Button.component'
import { Provider } from '@dataTypes/provider.types'
import { getFullName } from '@utils/fullname.util'
import type {
  ClearPlanProvidersModal,
  PendingBulkProvider,
  PlanProviderAssignModal,
} from './TreatmentPlanMain.types'

const procedureLabel = (count: number): string =>
  count === 1 ? 'procedure' : 'procedures'

export type TreatmentPlanProviderModalsProps = {
  planProviderAssignModal: PlanProviderAssignModal | null
  menuFillProviderId: string
  onMenuFillProviderIdChange: (value: string) => void
  onClosePlanProviderAssign: () => void
  onConfirmPlanProviderAssign: () => void
  clearPlanProvidersModal: ClearPlanProvidersModal | null
  onCloseClearPlanProviders: () => void
  onConfirmClearPlanProviders: () => void
  providerList: Provider[]
  pendingBulkProvider: PendingBulkProvider | null
  bulkProviderDontAskAgain: boolean
  onBulkProviderDontAskAgainChange: (checked: boolean) => void
  onBulkProviderModalHide: () => void
  onBulkProviderOnlyThisProcedure: () => void
  onConfirmBulkProviderApplyAll: () => void
}

const TreatmentPlanProviderModals: React.FC<
  TreatmentPlanProviderModalsProps
> = ({
  planProviderAssignModal,
  menuFillProviderId,
  onMenuFillProviderIdChange,
  onClosePlanProviderAssign,
  onConfirmPlanProviderAssign,
  clearPlanProvidersModal,
  onCloseClearPlanProviders,
  onConfirmClearPlanProviders,
  providerList,
  pendingBulkProvider,
  bulkProviderDontAskAgain,
  onBulkProviderDontAskAgainChange,
  onBulkProviderModalHide,
  onBulkProviderOnlyThisProcedure,
  onConfirmBulkProviderApplyAll,
}): JSX.Element => {
  return (
    <>
      <ConfirmationModal
        size="md"
        show={!!planProviderAssignModal}
        onHide={onClosePlanProviderAssign}
        title={
          planProviderAssignModal?.mode === 'replace'
            ? 'Replace providers on this plan'
            : 'Assign provider to unassigned procedures'
        }
        body={
          <div>
            {planProviderAssignModal?.mode === 'replace' ? (
              <p>
                Select a provider to apply to{' '}
                <strong>{planProviderAssignModal.affectedCount}</strong>{' '}
                {procedureLabel(planProviderAssignModal.affectedCount)} in this
                plan. Existing provider assignments will be replaced.
              </p>
            ) : (
              <p>
                Select a provider to apply to{' '}
                <strong>{planProviderAssignModal?.affectedCount}</strong>{' '}
                {procedureLabel(planProviderAssignModal?.affectedCount ?? 0)} in
                this plan that do not have a provider yet.
              </p>
            )}
            <Form.Group className="mb-0">
              <Form.Label className="mb-1">Provider</Form.Label>
              <Form.Select
                size="sm"
                value={menuFillProviderId}
                onChange={(e) => onMenuFillProviderIdChange(e.target.value)}
              >
                <option value="">Select provider</option>
                {providerList.map((option) => (
                  <option key={option.provider_id} value={option.provider_id}>
                    {`Dr. ${getFullName(option)}`}
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          </div>
        }
        footer={
          <>
            <Button
              size="sm"
              className="button-secondary"
              onClick={onClosePlanProviderAssign}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="button-primary"
              disabled={menuFillProviderId.trim() === ''}
              onClick={onConfirmPlanProviderAssign}
            >
              {planProviderAssignModal?.mode === 'replace'
                ? 'Replace all'
                : 'Assign to all unassigned'}
            </Button>
          </>
        }
      />
      <ConfirmationModal
        size="md"
        show={!!clearPlanProvidersModal}
        onHide={onCloseClearPlanProviders}
        title="Clear all providers"
        body={
          <p>
            Remove the provider from{' '}
            <strong>{clearPlanProvidersModal?.assignedCount}</strong>{' '}
            {procedureLabel(clearPlanProvidersModal?.assignedCount ?? 0)} in
            this plan. Each procedure needs a provider before this plan can be
            saved.
          </p>
        }
        footer={
          <>
            <Button
              size="sm"
              className="button-secondary"
              onClick={onCloseClearPlanProviders}
            >
              Cancel
            </Button>
            <Button
              size="sm"
              className="button-primary"
              onClick={onConfirmClearPlanProviders}
            >
              Clear providers
            </Button>
          </>
        }
      />
      <ConfirmationModal
        className="plan-add-provider-modal"
        size="md"
        show={!!pendingBulkProvider}
        onHide={onBulkProviderModalHide}
        title="Apply provider to remaining procedures?"
        body={
          <div>
            <p>
              Apply{' '}
              <strong>Dr. {pendingBulkProvider?.providerDisplayName}</strong> to{' '}
              <strong>{pendingBulkProvider?.emptyCount}</strong> other procedure
              {pendingBulkProvider && pendingBulkProvider.emptyCount === 1
                ? ''
                : 's'}{' '}
              in this plan that do not have a provider yet?
            </p>
            <Form.Check
              type="checkbox"
              id="tx-plan-bulk-provider-dont-ask"
              label="Don't ask again for this plan"
              checked={bulkProviderDontAskAgain}
              onChange={(e) =>
                onBulkProviderDontAskAgainChange(e.target.checked)
              }
            />
          </div>
        }
        footer={
          <>
            <Button
              type="button"
              size="sm"
              className="button-secondary"
              onClick={onBulkProviderOnlyThisProcedure}
            >
              Only this procedure
            </Button>
            <Button
              type="button"
              size="sm"
              className="button-primary"
              onClick={onConfirmBulkProviderApplyAll}
            >
              Apply to remaining procedures
            </Button>
          </>
        }
      />
    </>
  )
}

export default TreatmentPlanProviderModals
